import React, { useContext, useMemo, useState } from "react";
import { connect } from "react-redux";
import DOMPurify from "isomorphic-dompurify";
import { Context } from "@components/useContext/context";
import { executeActions, getWidgetSpecificProps } from "@utils/index";
import { formatToINR } from "@utils/inrFormatter";
import { resolvePath } from "@utils/resolvePath";
import * as S from "./styles";

const TP_PLAN_ID = "car_tp";
const TP_PRO_FULL_PLAN_ID = "car_tp_pro_full";
const TP_PRO_LITE_PLAN_ID = "car_tp_pro_lite";
const TP_PRO_PLAN_IDS = new Set([TP_PRO_FULL_PLAN_ID, TP_PRO_LITE_PLAN_ID]);

type PlanTag = {
  image?: string;
  text?: string;
  type?: string;
};

type PlanDescriptionItem = {
  text?: string;
  image?: string;
};

type LandingPlan = {
  id?: string;
  display_name?: string;
  display_premium?: string;
  premium?: number | string;
  review_plan_name?: string;
  secondary_tag?: PlanTag;
  tp_landing_description_items?: PlanDescriptionItem[];
};

type PlansPayload = {
  covers?: LandingPlan[];
};

type HeroTitleTemplate = {
  withPremiumDelta?: string;
  withoutPremiumDelta?: string;
};

type LandingCopy = {
  heroTitleByPlanId?: Record<string, HeroTitleTemplate>;
  premiumDeltaLabelTemplate?: string;
  upgradeBenefitsTitle?: string;
  selectPlanLabel?: string;
  editCardTitle?: string;
  editCardActionLabel?: string;
};

type ActionConfig = Record<string, Record<string, unknown>[]>;

type RenewalTpProLandingProps = {
  id: string;
  is_visible?: boolean;
  iconSrc?: string;
  iconAlt?: string;
  plans?: PlansPayload;
  copy?: LandingCopy;
  actions?: ActionConfig;
};

type ReduxState = {
  configuration?: {
    normalisedConfig?: unknown;
  };
};

const mapStateToProps = (state: ReduxState, ownProps: { id: string }) =>
  getWidgetSpecificProps(state.configuration?.normalisedConfig, ownProps.id);

const sanitize = (value: string) => ({
  __html: DOMPurify.sanitize(value)
});

const getAssetPath = (src?: string) => {
  if (!src) return "";
  if (/^https?:\/\//.test(src)) return src;
  return src.startsWith("/") ? resolvePath(src) : "";
};

const warnForMissingCopy = (message: string) => {
  if (process.env.NODE_ENV !== "production") {
    console.warn(`[renewal_tp_pro_landing] ${message}`);
  }
};

const interpolateTemplate = (
  template: string | undefined,
  values: Record<string, string>
) => {
  if (!template?.trim()) return "";

  let hasMissingToken = false;
  const interpolated = template.replace(
    /\{\{\s*([a-z_]+)\s*\}\}/gi,
    (_match, token: string) => {
      const value = values[token];
      if (!value) {
        hasMissingToken = true;
        return "";
      }
      return value;
    }
  );

  return hasMissingToken || /\{\{.*\}\}/.test(interpolated)
    ? ""
    : interpolated.trim();
};

const getDescriptionItems = (plan: LandingPlan): PlanDescriptionItem[] => {
  if (!Array.isArray(plan.tp_landing_description_items)) {
    warnForMissingCopy(
      `${plan.id || "unknown plan"} is missing tp_landing_description_items`
    );
    return [];
  }

  return plan.tp_landing_description_items.flatMap((item) => {
    const text = typeof item?.text === "string" ? item.text.trim() : "";
    if (!text) {
      warnForMissingCopy(
        `${plan.id || "unknown plan"} contains a blank description item`
      );
      return [];
    }
    return [{ text, image: item.image }];
  });
};

const getBadge = (plan: LandingPlan) => {
  const text =
    typeof plan.secondary_tag?.text === "string"
      ? plan.secondary_tag.text.trim()
      : "";
  if (!text) {
    warnForMissingCopy(
      `${plan.id || "unknown plan"} is missing secondary_tag.text`
    );
    return null;
  }

  return {
    text,
    variant:
      plan.secondary_tag?.type?.trim().toLowerCase() === "upgrade"
        ? ("upgrade" as const)
        : ("neutral" as const)
  };
};

const getPremiumDelta = (
  currentPlan: LandingPlan,
  upgradePlan: LandingPlan
) => {
  const currentPremium = Number(currentPlan.premium);
  const upgradePremium = Number(upgradePlan.premium);

  if (!Number.isFinite(currentPremium) || !Number.isFinite(upgradePremium)) {
    return null;
  }

  return Math.max(0, upgradePremium - currentPremium);
};

const getDisplayPremium = (plan: LandingPlan) => {
  const premium = Number(plan.premium);

  return Number.isFinite(premium)
    ? formatToINR(premium, true)
    : plan.display_premium || "";
};

const RenewalTpProLanding = ({
  is_visible,
  iconSrc,
  iconAlt,
  plans,
  copy,
  actions = {}
}: RenewalTpProLandingProps) => {
  const { isDesktop } = useContext(Context);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const planData = useMemo(() => {
    const currentPlan = plans?.covers?.find((plan) => plan.id === TP_PLAN_ID);
    const upgradePlan = plans?.covers?.find((plan) =>
      TP_PRO_PLAN_IDS.has(plan.id || "")
    );

    if (!currentPlan || !upgradePlan) return null;
    if (!currentPlan.display_premium || !upgradePlan.display_premium) {
      return null;
    }

    const premiumDelta = getPremiumDelta(currentPlan, upgradePlan);
    const formattedDelta =
      premiumDelta !== null && premiumDelta > 0
        ? formatToINR(premiumDelta, true)
        : "";
    const heroTemplates = copy?.heroTitleByPlanId?.[upgradePlan.id || ""];
    const heroTitle = interpolateTemplate(
      formattedDelta
        ? heroTemplates?.withPremiumDelta
        : heroTemplates?.withoutPremiumDelta,
      { premium_delta: formattedDelta }
    );
    const priceNudge = formattedDelta
      ? interpolateTemplate(copy?.premiumDeltaLabelTemplate, {
          premium_delta: formattedDelta
        })
      : "";

    return {
      currentPlan,
      upgradePlan,
      currentPlanPremium: getDisplayPremium(currentPlan),
      upgradePlanPremium: getDisplayPremium(upgradePlan),
      heroTitle,
      priceNudge,
      currentBenefits: getDescriptionItems(currentPlan),
      upgradeBenefits: getDescriptionItems(upgradePlan)
    };
  }, [copy, plans]);

  if (!is_visible) return null;

  if (!planData) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        "renewal_tp_pro_landing requires car_tp and one TP Pro plan with display prices"
      );
    }
    return null;
  }

  const handlePlanSelect = (plan: LandingPlan) => {
    const actionConfig = actions.plan_select_on_click || [];
    if (isSubmitting || !plan.id || actionConfig.length === 0) return;

    setIsSubmitting(true);
    executeActions(actionConfig, {
      data: {
        plan_id: plan.id,
        display_name_sdui: plan.review_plan_name || plan.display_name
      }
    });
  };

  const handleEdit = () => {
    const actionConfig = actions.edit_on_click || [];
    if (isSubmitting || actionConfig.length === 0) return;

    setIsSubmitting(true);
    executeActions(actionConfig);
  };

  const {
    currentPlan,
    upgradePlan,
    currentPlanPremium,
    upgradePlanPremium,
    currentBenefits,
    upgradeBenefits,
    heroTitle,
    priceNudge
  } = planData;
  const currentBadge = getBadge(currentPlan);
  const upgradeBadge = getBadge(upgradePlan);
  const fallbackTick = getAssetPath("/images/tick_do_yes_proceed.png");
  const selectPlanLabel = copy?.selectPlanLabel?.trim() || "";
  const editCardTitle = copy?.editCardTitle?.trim() || "";
  const editCardActionLabel = copy?.editCardActionLabel?.trim() || "";

  if (!heroTitle)
    warnForMissingCopy("hero title template could not be resolved");
  if (!selectPlanLabel) warnForMissingCopy("selectPlanLabel is missing");
  if (!editCardActionLabel)
    warnForMissingCopy("editCardActionLabel is missing");

  return (
    <S.Preview aria-busy={isSubmitting}>
      {iconSrc ? (
        <S.HeroIcon
          $isDesktop={isDesktop}
          src={getAssetPath(iconSrc)}
          alt={iconAlt || ""}
        />
      ) : null}
      {heroTitle ? <S.Title $isDesktop={isDesktop}>{heroTitle}</S.Title> : null}

      <S.Plans>
        <S.Plan>
          <S.PlanHead>
            <S.PlanName>
              {currentBadge ? (
                <S.Badge $variant={currentBadge.variant}>
                  {currentBadge.text}
                </S.Badge>
              ) : null}
              {currentPlan.display_name ? (
                <S.PlanTitle>{currentPlan.display_name}</S.PlanTitle>
              ) : null}
            </S.PlanName>
            <S.PriceBlock>
              <S.Premium>{currentPlanPremium}</S.Premium>
            </S.PriceBlock>
          </S.PlanHead>

          {currentBenefits.length ? (
            <S.Benefits>
              {currentBenefits.map((benefit, index) => (
                <S.Benefit key={`${currentPlan.id}-benefit-${index}`}>
                  <S.BenefitIcon
                    src={getAssetPath(benefit.image) || fallbackTick}
                    alt=""
                  />
                  <S.BenefitText
                    dangerouslySetInnerHTML={sanitize(benefit.text || "")}
                  />
                </S.Benefit>
              ))}
            </S.Benefits>
          ) : null}

          {selectPlanLabel ? (
            <S.SelectButton
              type="button"
              disabled={isSubmitting}
              onClick={() => handlePlanSelect(currentPlan)}
            >
              {selectPlanLabel}
            </S.SelectButton>
          ) : null}
        </S.Plan>

        <S.Plan $selected>
          <S.PlanHead>
            <S.PlanName>
              {upgradeBadge ? (
                <S.Badge $variant={upgradeBadge.variant}>
                  {upgradeBadge.text}
                </S.Badge>
              ) : null}
              {upgradePlan.display_name ? (
                <S.PlanTitle>{upgradePlan.display_name}</S.PlanTitle>
              ) : null}
            </S.PlanName>
            <S.PriceBlock>
              <S.Premium>{upgradePlanPremium}</S.Premium>
              {priceNudge ? <S.PriceNudge>{priceNudge}</S.PriceNudge> : null}
            </S.PriceBlock>
          </S.PlanHead>

          {copy?.upgradeBenefitsTitle || upgradeBenefits.length ? (
            <S.Highlight>
              {copy?.upgradeBenefitsTitle ? (
                <S.HighlightTitle $isDesktop={isDesktop}>
                  {copy.upgradeBenefitsTitle}
                </S.HighlightTitle>
              ) : null}
              {upgradeBenefits.length ? (
                <S.Benefits>
                  {upgradeBenefits.map((benefit, index) => (
                    <S.Benefit key={`${upgradePlan.id}-benefit-${index}`}>
                      <S.BenefitIcon
                        src={getAssetPath(benefit.image) || fallbackTick}
                        alt=""
                      />
                      <S.BenefitText
                        dangerouslySetInnerHTML={sanitize(benefit.text || "")}
                      />
                    </S.Benefit>
                  ))}
                </S.Benefits>
              ) : null}
            </S.Highlight>
          ) : null}

          {selectPlanLabel ? (
            <S.SelectButton
              type="button"
              disabled={isSubmitting}
              onClick={() => handlePlanSelect(upgradePlan)}
            >
              {selectPlanLabel}
            </S.SelectButton>
          ) : null}
        </S.Plan>
      </S.Plans>

      {editCardActionLabel ? (
        <S.ExploreButton
          type="button"
          disabled={isSubmitting}
          onClick={handleEdit}
        >
          {editCardTitle ? (
            <S.ExploreTitle>{editCardTitle}</S.ExploreTitle>
          ) : null}
          <S.ExploreLabel>{editCardActionLabel}</S.ExploreLabel>
        </S.ExploreButton>
      ) : null}
    </S.Preview>
  );
};

export default connect(mapStateToProps)(RenewalTpProLanding);
